/**
 * Bulk Import Context
 * Global state management for bulk import jobs with background tracking.
 * Now includes retry logic for API calls to handle transient failures.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

const BulkImportContext = createContext(null);

export const useBulkImport = () => {
  const context = useContext(BulkImportContext);
  if (!context) {
    throw new Error('useBulkImport must be used within a BulkImportProvider');
  }
  return context;
};

/**
 * Retry wrapper for API calls with exponential backoff
 */
const withRetry = async (fn, options = {}) => {
  const { maxRetries = 3, baseDelay = 500, silent = false } = options;
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on 4xx errors (client errors) except 404 which might be timing
      if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 404) {
        throw error;
      }
      
      // Wait before retry with exponential backoff
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

export const BulkImportProvider = ({ children }) => {
  // Active jobs being tracked
  const [jobs, setJobs] = useState([]);
  
  // Currently open modal job
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJobData, setActiveJobData] = useState(null);
  
  // Modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  
  // Polling interval ref
  const pollingRef = useRef(null);
  
  // Fetch jobs function - defined before useEffect that uses it
  const fetchJobs = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/bulk/v2/jobs');
      const newJobs = response.data || [];
      
      // Check for newly completed jobs
      setJobs(prevJobs => {
        newJobs.forEach(newJob => {
          const oldJob = prevJobs.find(j => j.job_id === newJob.job_id);
          if (oldJob && oldJob.status === 'classifying' && newJob.status === 'review') {
            toast.success(`Classification complete for "${newJob.name}"`, {
              description: `${newJob.classified_count} parts classified`
            });
          } else if (oldJob && oldJob.status === 'creating' && newJob.status === 'completed') {
            toast.success(`Import complete: "${newJob.name}"`, {
              description: `${newJob.created_count} parts created`
            });
          }
        });
        return newJobs;
      });
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  }, []);
  
  // Fetch all jobs on mount
  useEffect(() => {
    fetchJobs();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchJobs]);
  
  // Poll for job updates when there are active jobs
  useEffect(() => {
    const activeJobs = jobs.filter(j => 
      j.status === 'classifying' || j.status === 'creating'
    );
    
    if (activeJobs.length > 0) {
      pollingRef.current = setInterval(() => {
        fetchJobs();
      }, 2000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [jobs, fetchJobs]);
  
  const fetchJobDetail = useCallback(async (jobId, options = {}) => {
    const { silent = false } = options;
    try {
      const response = await withRetry(
        () => api.get(`/api/v1/bulk/v2/jobs/${jobId}`),
        { maxRetries: 3, baseDelay: 500, silent }
      );
      setActiveJobData(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch job detail:', error);
      // Only show toast if not silent (during polling, we stay silent)
      if (!silent) {
        toast.error('Failed to load job details');
      }
      return null;
    }
  }, []);
  
  const createJob = useCallback(async (parts, name) => {
    try {
      const response = await api.post('/api/v1/bulk/v2/jobs', { parts, name });
      const job = response.data;
      setJobs(prev => [job, ...prev]);
      setActiveJobId(job.job_id);
      await fetchJobDetail(job.job_id);
      return job;
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create import job');
      throw error;
    }
  }, [fetchJobDetail]);
  
  const startClassification = useCallback(async (jobId) => {
    try {
      await withRetry(
        () => api.post(`/api/v1/bulk/v2/jobs/${jobId}/classify`),
        { maxRetries: 3, baseDelay: 500 }
      );
      toast.info('AI classification started', {
        description: 'You can minimize this and continue working'
      });
      // Fetch both jobs list and active job detail to start polling
      fetchJobs();
      // Use silent mode for initial fetch after starting - data may not be ready yet
      await fetchJobDetail(jobId, { silent: true });
    } catch (error) {
      console.error('Failed to start classification:', error);
      toast.error('Failed to start classification');
      throw error;
    }
  }, [fetchJobs, fetchJobDetail]);
  
  const startCreation = useCallback(async (jobId) => {
    try {
      await withRetry(
        () => api.post(`/api/v1/bulk/v2/jobs/${jobId}/create`),
        { maxRetries: 3, baseDelay: 500 }
      );
      toast.info('Creating parts...', {
        description: 'Parts are being added to the catalogue'
      });
      // Fetch both to start tracking creation
      fetchJobs();
      await fetchJobDetail(jobId, { silent: true });
    } catch (error) {
      console.error('Failed to start creation:', error);
      toast.error('Failed to start part creation');
      throw error;
    }
  }, [fetchJobs, fetchJobDetail]);
  
  const updatePart = useCallback(async (jobId, partIndex, updates) => {
    try {
      const response = await api.put(`/api/v1/bulk/v2/jobs/${jobId}/parts/${partIndex}`, updates);
      // Update local state
      setActiveJobData(prev => {
        if (!prev) return prev;
        const newParts = [...prev.parts];
        newParts[partIndex] = { ...newParts[partIndex], ...response.data };
        return { ...prev, parts: newParts };
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update part:', error);
      toast.error('Failed to update classification');
      throw error;
    }
  }, []);
  
  const skipPart = useCallback(async (jobId, partIndex) => {
    try {
      await api.post(`/api/v1/bulk/v2/jobs/${jobId}/parts/${partIndex}/skip`);
      await fetchJobDetail(jobId);
    } catch (error) {
      console.error('Failed to skip part:', error);
      toast.error('Failed to skip part');
    }
  }, [fetchJobDetail]);
  
  const bulkSelect = useCallback(async (jobId, indices, selected) => {
    try {
      await api.post(`/api/v1/bulk/v2/jobs/${jobId}/bulk-select`, { indices, selected });
      await fetchJobDetail(jobId);
    } catch (error) {
      console.error('Failed to update selection:', error);
    }
  }, [fetchJobDetail]);
  
  const trustHighConfidence = useCallback(async (jobId) => {
    try {
      const response = await api.post(`/api/v1/bulk/v2/jobs/${jobId}/trust-high-confidence`);
      toast.success(`${response.data.count} high-confidence parts selected`);
      await fetchJobDetail(jobId);
    } catch (error) {
      console.error('Failed to trust high confidence:', error);
    }
  }, [fetchJobDetail]);
  
  const cancelJob = useCallback(async (jobId) => {
    try {
      await api.post(`/api/v1/bulk/v2/jobs/${jobId}/cancel`);
      toast.info('Import cancelled');
      fetchJobs();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel import');
    }
  }, [fetchJobs]);
  
  const deleteJob = useCallback(async (jobId) => {
    try {
      await api.delete(`/api/v1/bulk/v2/jobs/${jobId}`);
      setJobs(prev => prev.filter(j => j.job_id !== jobId));
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  }, []);
  
  const openModal = useCallback((jobId = null) => {
    setActiveJobId(jobId);
    setActiveJobData(null);
    setIsModalOpen(true);
    if (jobId) {
      fetchJobDetail(jobId);
    }
  }, [fetchJobDetail]);
  
  const openJob = useCallback(async (jobId) => {
    setActiveJobId(jobId);
    setIsModalOpen(true);
    // Use retry with non-silent mode since user explicitly requested to open
    await fetchJobDetail(jobId, { silent: false });
  }, [fetchJobDetail]);
  
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    // Don't clear activeJobId - keep it for background tracking
  }, []);
  
  const minimizeToBackground = useCallback(() => {
    setIsModalOpen(false);
    setIsDrawerExpanded(true);
    toast.info('Import continues in background', {
      description: 'Click the status panel to return'
    });
  }, []);
  
  // Count active jobs
  const activeJobCount = jobs.filter(j => 
    j.status === 'classifying' || j.status === 'creating'
  ).length;
  
  const value = {
    // State
    jobs,
    activeJobId,
    activeJobData,
    isModalOpen,
    isDrawerExpanded,
    activeJobCount,
    
    // Actions
    fetchJobs,
    fetchJobDetail,
    createJob,
    startClassification,
    startCreation,
    updatePart,
    skipPart,
    bulkSelect,
    trustHighConfidence,
    cancelJob,
    deleteJob,
    openModal,
    openJob,
    closeModal,
    minimizeToBackground,
    setIsDrawerExpanded,
  };
  
  return (
    <BulkImportContext.Provider value={value}>
      {children}
    </BulkImportContext.Provider>
  );
};

export default BulkImportContext;
